import { Link as RemixLink } from "@remix-run/react";

import {
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Text,
  Heading,
  FormHelperText,
  Flex,
  Link,
  Button,
  IconButton,
  Icon,
  Switch,
} from "@chakra-ui/react";
import { MdTextsms, MdEmail, MdNotifications } from "react-icons/md";
import { SiWhatsapp } from "react-icons/si";

import { useUserSubscriptionValue } from "#app/hooks/index.js";
import { useEmailSuggestion } from "#app/hooks/useEmailSuggestion.js";

import { EMAIL_REGEX } from "#app/utils/regex.js";

export function EmailInput({ name, formContext, ...rest }) {
  const {
    register,
    formState: { errors },
  } = formContext;

  const { suggestion, checkEmail, clearSuggestion } = useEmailSuggestion();

  return useUserSubscriptionValue({
    none: () => (
      <FormControl isInvalid={!!errors.email} {...rest} isRequired>
        <FormLabel>Where should we send notifications?</FormLabel>
        <Input
          name={name}
          type="email"
          autoComplete="email"
          {...register(name, {
            required: {
              value: true,
              message: "Please enter an email address",
            },
            pattern: {
              value: EMAIL_REGEX,
              message: "Invalid email address",
            },
            validate: (email) => {
              if (email.includes("+")) {
                return 'Email cannot contain a "+" symbol';
              }
            },
            // Email is the form's top validation failure for anonymous
            // users — surface bad addresses on blur instead of at submit.
            // Server errors (e.g. "account already exists") must survive a
            // blur: re-triggering client validation would clear them the
            // moment focus leaves the field.
            onBlur: (e) => {
              checkEmail(e.target.value);
              if (e.target.value && errors[name]?.type !== "server") {
                formContext.trigger(name);
              }
            },
            onChange: () => {
              clearSuggestion();
              if (errors[name]) formContext.trigger(name);
            },
          })}
        />
        {errors.email ? (
          errors.email.message.includes(
            "An account with this email already exists",
          ) ? (
            <FormErrorMessage>
              <Text>
                An account with this email already exists. To add a request to
                this account,{" "}
                <Link as={RemixLink} to="/login">
                  please log in and try again.
                </Link>
              </Text>
            </FormErrorMessage>
          ) : (
            <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
          )
        ) : suggestion ? (
          <Text fontSize="sm" color="warning" mt={2}>
            Did you mean{" "}
            <Link
              color="warning"
              fontWeight="bold"
              textDecoration="underline"
              cursor="pointer"
              onClick={() => {
                formContext.setValue(name, suggestion, {
                  shouldValidate: true,
                });
                clearSuggestion();
              }}
            >
              {suggestion}
            </Link>
            ?
          </Text>
        ) : (
          <FormHelperText>
            <Text mb={2}>
              We'll create a free account when you submit this request — no
              password and no credit card needed.{" "}
              <Link as={RemixLink} to="/login">
                Already have an account?
              </Link>
            </Text>
          </FormHelperText>
        )}
      </FormControl>
    ),
    trial: (user) => (
      <>
        <FormControl isInvalid={!!errors.email} {...rest}>
          <FormLabel htmlFor="email">
            You'll receive email notifications at:
          </FormLabel>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Icon as={MdEmail} color="accent" />
            </InputLeftElement>
            <Input
              isDisabled
              id="email"
              name="email"
              type="email"
              value={user.email}
              pl={10}
            />
          </InputGroup>
          {errors.email ? (
            <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
          ) : null}
        </FormControl>
      </>
    ),
    active: (user) => {
      const emailEnabled = formContext.watch("emailEnabled");
      const smsEnabled = formContext.watch("smsEnabled");
      const whatsappEnabled = formContext.watch("whatsappEnabled");
      const pushEnabled = formContext.watch("pushEnabled");

      const hasPhone = !!user.phone;
      const hasWhatsapp = !!user.whatsapp;
      const hasPush = user.pushSubscriptions?.length > 0;

      // Check if this would be the last enabled channel
      const countEnabled = [
        emailEnabled,
        hasPhone && smsEnabled,
        hasWhatsapp && whatsappEnabled,
        hasPush && pushEnabled,
      ].filter(Boolean).length;

      const isLastEnabled = countEnabled <= 1;

      const toggleEmail = () => {
        if (emailEnabled && isLastEnabled) return;
        formContext.setValue("emailEnabled", !emailEnabled, {
          shouldDirty: true,
        });
      };
      const toggleSms = () => {
        if (smsEnabled && isLastEnabled) return;
        formContext.setValue("smsEnabled", !smsEnabled, {
          shouldDirty: true,
        });
      };
      const toggleWhatsapp = () => {
        if (whatsappEnabled && isLastEnabled) return;
        formContext.setValue("whatsappEnabled", !whatsappEnabled, {
          shouldDirty: true,
        });
      };
      const togglePush = () => {
        if (pushEnabled && isLastEnabled) return;
        formContext.setValue("pushEnabled", !pushEnabled, {
          shouldDirty: true,
        });
      };

      return (
        <>
          <FormControl isInvalid={!!errors.email} {...rest}>
            <input type="hidden" {...register("emailEnabled")} />
            <FormLabel htmlFor="email">
              You{" "}
              <Button
                variant="link"
                colorScheme="brand"
                verticalAlign="baseline"
                fontSize="inherit"
                fontWeight="inherit"
                minW="auto"
                onClick={toggleEmail}
                isDisabled={emailEnabled && isLastEnabled}
              >
                {emailEnabled ? "will" : "won't"}
              </Button>{" "}
              receive email notifications at:
            </FormLabel>
            <InputGroup>
              <InputLeftElement>
                <IconButton
                  icon={<Icon as={MdEmail} />}
                  aria-label="Toggle email"
                  variant="ghost"
                  size="sm"
                  color={emailEnabled ? "accent" : "subtle"}
                  onClick={toggleEmail}
                  isDisabled={emailEnabled && isLastEnabled}
                />
              </InputLeftElement>
              <Input
                isDisabled
                id="email"
                name="email"
                type="email"
                value={user.email}
                pl={10}
                pr={16}
                {...(!emailEnabled && {
                  textDecoration: "line-through",
                  color: "subtle",
                })}
              />
              <InputRightElement w={14}>
                <Switch
                  colorScheme="brand"
                  size="sm"
                  isChecked={!!emailEnabled}
                  onChange={toggleEmail}
                  isDisabled={emailEnabled && isLastEnabled}
                />
              </InputRightElement>
            </InputGroup>
            {errors.email ? (
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
            ) : null}
          </FormControl>
          {user.phone && (
            <FormControl isInvalid={false} mt={6} {...rest}>
              <input type="hidden" {...register("smsEnabled")} />
              <FormLabel htmlFor="phone">
                You{" "}
                <Button
                  variant="link"
                  colorScheme="brand"
                  verticalAlign="baseline"
                  fontSize="inherit"
                  fontWeight="inherit"
                  minW="auto"
                  onClick={toggleSms}
                  isDisabled={smsEnabled && isLastEnabled}
                >
                  {smsEnabled ? "will" : "won't"}
                </Button>{" "}
                receive text notifications at:
              </FormLabel>
              <InputGroup>
                <InputLeftElement>
                  <IconButton
                    icon={<Icon as={MdTextsms} />}
                    aria-label="Toggle SMS"
                    variant="ghost"
                    size="sm"
                    color={smsEnabled ? "accent" : "subtle"}
                    onClick={toggleSms}
                    isDisabled={smsEnabled && isLastEnabled}
                  />
                </InputLeftElement>
                <Input
                  isDisabled
                  id="phone"
                  name="phone"
                  type="phone"
                  value={user.phone}
                  pl={10}
                  pr={16}
                  {...(!smsEnabled && {
                    textDecoration: "line-through",
                    color: "subtle",
                  })}
                />
                <InputRightElement w={14}>
                  <Switch
                    colorScheme="brand"
                    size="sm"
                    isChecked={!!smsEnabled}
                    onChange={toggleSms}
                    isDisabled={smsEnabled && isLastEnabled}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>
          )}
          {user.whatsapp && (
            <FormControl isInvalid={false} mt={6} {...rest}>
              <input type="hidden" {...register("whatsappEnabled")} />
              <FormLabel htmlFor="whatsapp">
                You{" "}
                <Button
                  variant="link"
                  colorScheme="brand"
                  verticalAlign="baseline"
                  fontSize="inherit"
                  fontWeight="inherit"
                  minW="auto"
                  onClick={toggleWhatsapp}
                  isDisabled={whatsappEnabled && isLastEnabled}
                >
                  {whatsappEnabled ? "will" : "won't"}
                </Button>{" "}
                receive WhatsApp notifications at:
              </FormLabel>
              <InputGroup>
                <InputLeftElement>
                  <IconButton
                    icon={<Icon as={SiWhatsapp} />}
                    aria-label="Toggle WhatsApp"
                    variant="ghost"
                    size="sm"
                    color={whatsappEnabled ? "accent" : "subtle"}
                    onClick={toggleWhatsapp}
                    isDisabled={whatsappEnabled && isLastEnabled}
                  />
                </InputLeftElement>
                <Input
                  isDisabled
                  id="whatsapp"
                  name="whatsapp"
                  type="whatsapp"
                  value={user.whatsapp}
                  pl={10}
                  pr={16}
                  {...(!whatsappEnabled && {
                    textDecoration: "line-through",
                    color: "subtle",
                  })}
                />
                <InputRightElement w={14}>
                  <Switch
                    colorScheme="brand"
                    size="sm"
                    isChecked={!!whatsappEnabled}
                    onChange={toggleWhatsapp}
                    isDisabled={whatsappEnabled && isLastEnabled}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>
          )}
          {user.pushSubscriptions?.length > 0 && (
            <FormControl mt={6} {...rest}>
              <input type="hidden" {...register("pushEnabled")} />
              <FormLabel>
                You{" "}
                <Button
                  variant="link"
                  colorScheme="brand"
                  verticalAlign="baseline"
                  fontSize="inherit"
                  fontWeight="inherit"
                  minW="auto"
                  onClick={togglePush}
                  isDisabled={pushEnabled && isLastEnabled}
                >
                  {pushEnabled ? "will" : "won't"}
                </Button>{" "}
                receive push notifications on:
              </FormLabel>
              <InputGroup>
                <InputLeftElement>
                  <IconButton
                    icon={<Icon as={MdNotifications} />}
                    aria-label="Toggle push notifications"
                    variant="ghost"
                    size="sm"
                    color={pushEnabled ? "accent" : "subtle"}
                    onClick={togglePush}
                    isDisabled={pushEnabled && isLastEnabled}
                  />
                </InputLeftElement>
                <Input
                  isDisabled
                  value={user.pushSubscriptions
                    .map((sub, i) => sub.nickname || `Device ${i + 1}`)
                    .join(", ")}
                  pl={10}
                  pr={16}
                  textOverflow="ellipsis"
                  overflow="hidden"
                  whiteSpace="nowrap"
                  {...(!pushEnabled && {
                    textDecoration: "line-through",
                    color: "subtle",
                  })}
                />
                <InputRightElement w={14}>
                  <Switch
                    colorScheme="brand"
                    size="sm"
                    isChecked={!!pushEnabled}
                    onChange={togglePush}
                    isDisabled={pushEnabled && isLastEnabled}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>
          )}
        </>
      );
    },
  });
}
